import { useMemo, useState } from 'react';
import { Copy, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';

/**
 * Drop-in request/response viewer used by every observability detail
 * sheet (outbound API calls, inbound webhooks, outbound webhook
 * deliveries, inbound API errors). Renders an HTTP-tool-style panel
 * with Headers + Body sections per side and a copy-to-clipboard
 * button on each.
 *
 * All inputs are optional — pass only what you have. When neither
 * side has any data, the panel renders an empty-state hint so it's
 * obvious capture isn't there vs the field being literally empty.
 */
export interface RequestResponsePanelProps {
  request?: {
    method?: string | null;
    url?: string | null;
    headers?: Record<string, string> | null;
    body?: string | null;
  };
  response?: {
    status?: number | null;
    headers?: Record<string, string> | null;
    body?: string | null;
  };
  /** Hint text when neither side has any captured data. */
  emptyHint?: string;
}

export function RequestResponsePanel({
  request,
  response,
  emptyHint = 'No request/response capture recorded for this row.',
}: RequestResponsePanelProps) {
  const [tab, setTab] = useState<'request' | 'response'>(
    response && (response.body || response.headers || response.status != null)
      ? 'response'
      : 'request',
  );

  const hasRequest = !!(
    request && (request.url || request.headers || request.body)
  );
  const hasResponse = !!(
    response && (response.body || response.headers || response.status != null)
  );

  if (!hasRequest && !hasResponse) {
    return (
      <div className="rounded-md border border-dashed border-border/70 px-4 py-6 text-center text-[12px] text-muted-foreground">
        {emptyHint}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 rounded-md bg-muted p-1">
        <TabButton active={tab === 'request'} onClick={() => setTab('request')} disabled={!hasRequest}>
          Request
        </TabButton>
        <TabButton active={tab === 'response'} onClick={() => setTab('response')} disabled={!hasResponse}>
          Response{response?.status != null ? ` · ${response.status}` : ''}
        </TabButton>
      </div>

      {tab === 'request' && hasRequest && (
        <div className="space-y-3">
          {(request?.method || request?.url) && (
            <CodeBlock
              label="URL"
              text={
                request?.method
                  ? `${request.method} ${request.url ?? ''}`
                  : (request?.url ?? '')
              }
              mono
            />
          )}
          {request?.headers && Object.keys(request.headers).length > 0 && (
            <CodeBlock
              label="Headers"
              text={formatHeaders(request.headers)}
              mono
            />
          )}
          {request?.body && <CodeBlock label="Body" text={request.body} />}
        </div>
      )}

      {tab === 'response' && hasResponse && (
        <div className="space-y-3">
          {response?.headers && Object.keys(response.headers).length > 0 && (
            <CodeBlock
              label="Headers"
              text={formatHeaders(response.headers)}
              mono
            />
          )}
          {response?.body && <CodeBlock label="Body" text={response.body} />}
          {!response?.headers && !response?.body && response?.status != null && (
            <div className="rounded-md border border-dashed border-border/70 px-4 py-4 text-center text-[12px] text-muted-foreground">
              HTTP {response.status} — no body captured.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TabButton({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex-1 rounded px-3 py-1.5 text-[11.5px] font-medium transition-colors ${
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground'
      } ${disabled ? 'cursor-not-allowed opacity-40' : ''}`}
    >
      {children}
    </button>
  );
}

function CodeBlock({
  label,
  text,
  mono = false,
}: {
  label: string;
  text: string;
  mono?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const pretty = useMemo(() => prettyPrintIfJson(text), [text]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(pretty);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={copy}
          className="h-6 gap-1 px-2 text-[10.5px] text-muted-foreground hover:text-foreground"
        >
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <pre
        className={`max-h-[280px] overflow-auto rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-[11.5px] leading-snug ${
          mono ? 'font-mono' : 'font-mono'
        } whitespace-pre-wrap break-words`}
      >
        {pretty}
      </pre>
    </div>
  );
}

function formatHeaders(headers: Record<string, string>): string {
  return Object.entries(headers)
    .map(([k, v]) => `${k}: ${v}`)
    .join('\n');
}

/**
 * Best-effort pretty-print: if the input parses as JSON, return
 * `JSON.stringify(..., null, 2)`. Otherwise return as-is. Keeps SOAP
 * envelopes, form-encoded bodies, and plain text legible.
 */
function prettyPrintIfJson(s: string): string {
  const trimmed = s.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return s;
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return s;
  }
}
